// Single source of truth for the image formats accepted on upload across the app
// (dashboard drop, editor drop/panel, batch, photoshoot, AI studio).
// Add a new format here once and every picker, drop gate, and filter follows.
//
// HEIC/HEIF are accepted here but can't render in <img>/canvas — transcode them
// with `readImageFileAsCanvasReadyDataUrl` (imageProcessingUtils) after selection.

/** Extensions (no dot) used to build file-picker `accept` and as a name fallback. */
export const SUPPORTED_IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'jfif', 'webp', 'heic', 'heif'] as const

/** MIME types browsers report for supported uploads (incl. legacy jpeg aliases). */
const SUPPORTED_IMAGE_MIME_TYPES = new Set<string>([
  'image/png',
  'image/jpg',
  'image/jpeg',
  'image/jfif',
  'image/pjpeg',
  'image/pjp',
  'image/webp',
  'image/heic',
  'image/heif',
])

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

// Filename fallback: some browsers report an empty `type` for HEIC/HEIF (and
// occasionally for drag-and-drop), so match on extension too. Built from
// SUPPORTED_IMAGE_EXTENSIONS so upload gates and stripImageExtension cannot drift.
export const SUPPORTED_IMAGE_EXTENSION_PATTERN = new RegExp(
  `\\.(${SUPPORTED_IMAGE_EXTENSIONS.map(escapeRegExp).join('|')})$`,
  'i',
)

/** Value for an <input type="file"> `accept` attribute / property. */
export const IMAGE_UPLOAD_ACCEPT = [
  ...SUPPORTED_IMAGE_MIME_TYPES,
  ...SUPPORTED_IMAGE_EXTENSIONS.map((ext) => `.${ext}`),
].join(', ')

/** True when a selected/dropped file is a supported image (by MIME or extension). */
export function isSupportedImageFile(file: File): boolean {
  return SUPPORTED_IMAGE_MIME_TYPES.has(file.type) || SUPPORTED_IMAGE_EXTENSION_PATTERN.test(file.name)
}

// Cheap sync check so callers can skip the fetch()+blob()+magic-byte sniff in
// mediaImageRepositoryProcessing.isHeic() for the common (non-HEIC) upload case.
const HEIC_MIME_TYPES = new Set(['image/heic', 'image/heif'])
const HEIC_EXTENSION_PATTERN = /\.hei[cf]$/i

/** True when a file's MIME type or extension indicates HEIC/HEIF. Best-effort: a false
 * negative (e.g. a mislabeled file) still gets caught by the byte-sniffing isHeic() fallback
 * wherever callers keep that as a safety net. */
export function looksLikeHeic(file: File): boolean {
  return HEIC_MIME_TYPES.has(file.type) || HEIC_EXTENSION_PATTERN.test(file.name)
}
